import React, { useState, useEffect, useMemo, memo } from 'react';
import {
  Form,
  Row,
  Col,
  Input,
  Space,
  Button,
  DatePicker,
  Radio,
  Select,
} from 'antd';
import type { SPFieldProps, ColumnRenderConfig } from './types';
import _ from 'lodash';
import moment from 'moment';
import useValuesStore from '@/store/useValuesStore';
import useMetaStore from '@/store/useMetaStore';
import ErrorBoundary from './ErrorBoundary';
import { FormFrom, FormMode } from './types';
import { getPlaceHolder } from '../common/utils';
import useTooltip from './useTooltip';

function DatePickerWarp({ value, onChange, ...otherProps }: any) {
  const _value = value && moment(value);
  return <DatePicker value={_value} onChange={onChange} {...otherProps} />;
}

function RadioWarp({ value, onChange, request, params, ...otherProps }: any) {
  const [options, setOptions] = useState<any[]>([]);

  useEffect(() => {
    request &&
      request(params).then((res: any) => {
        setOptions(res);
      });
  }, [request, JSON.stringify(params)]);

  return (
    <Radio.Group
      value={value}
      onChange={onChange}
      options={options}
      {...otherProps}
    />
  );
}

function SelectWarp({ value, onChange, request, params, ...otherProps }: any) {
  const [options, setOptions] = useState<any[]>([]);

  useEffect(() => {
    request &&
      request(params).then((res: any) => {
        setOptions(res);
      });
  }, [request, JSON.stringify(params)]);

  return (
    <Select
      value={value}
      onChange={onChange}
      options={options}
      {...otherProps}
    />
  );
}

function Field(props: SPFieldProps) {
  const {
    form,
    from,
    label,
    valueType,
    name,
    render,
    formItemProps,
    fieldProps,
    style,
    request,
    requestParams,
    disabled,
    required,
    formValue,
    rowIndex,
    labelCol,
    wrapperCol,
    mode = FormMode.Edit,
    value: propsValue,
    onChange,
    renderConfig,
    moduleField,
    formItemClassName,
  } = props;

  const { setRules, resetValue, values, setValues } = useValuesStore();
  const metaStore = useMetaStore();
  const value = _.get(formValue, name);
  const columnsRenderConfig: ColumnRenderConfig = {
    from,
    name:
      from === FormFrom.Table && _.isArray(name)
        ? name.slice(1, name.length)
        : name,
    value: propsValue || value,
    formValue: from === FormFrom.Table ? formValue?._data : formValue,
    storeValue: values || {},
    rowIndex,
    rowValue: formValue?._data?.[rowIndex!],
    metaStore,
    ...(renderConfig || {}),
    // setStoreValue: setValues,
    // setRowValue: (index: number, dtat: any) {

    // }
    // 表格情况下生效
    // recordKey: rowIndex, // 临时兼容，后续删除
    // recordValue: _.get(formValue, rowIndex), // 临时兼容，后续删除
  };
  let _fieldProps: any = _.isFunction(fieldProps)
    ? fieldProps(form, columnsRenderConfig)
    : fieldProps;
  let _formItemProps: any = _.isFunction(formItemProps)
    ? formItemProps(form, columnsRenderConfig)
    : formItemProps;

  // 外部传值的情况下才进行覆盖
  if (required === true) {
    const rules = _formItemProps?.rules || [];
    if (!rules.find((x: any) => x.required)) {
      rules.push({
        required: true,
        message: '此项为必填项',
      });
    }
    _formItemProps = { ..._formItemProps, rules };
  }
  // props上的状态优先级最高
  if (disabled === true || disabled === false) {
    _fieldProps = { ..._fieldProps, disabled };
  }

  let Control: React.ReactNode;
  if (mode === FormMode.Edit) {
    if (render) {
      Control = render(value, { ...columnsRenderConfig }, { ...form });
    } else if (valueType) {
      switch (valueType) {
        case 'date':
          Control = <DatePickerWarp />;
          break;
        case 'dateTime':
          Control = <DatePickerWarp showTime />;
          break;
        case 'radio':
          Control = <RadioWarp request={request} params={requestParams} />;
          break;
        case 'select': // http://uat.crius.com/pro/lcp/task/TASK20221111140025311060/list
          Control = (
            <SelectWarp
              placeholder="请选择"
              request={request}
              params={requestParams}
              allowClear
            />
          );
          break;
        default:
          Control = <Input placeholder={getPlaceHolder(label)} allowClear />;
      }
    } else {
      Control = <Input placeholder={getPlaceHolder(label)} allowClear />;
    }
    if (React.isValidElement(Control)) {
      Control = React.cloneElement(Control as any, { ..._fieldProps });
    }
  } else if (mode === FormMode.Read) {
    if (render) {
      Control = render(value, { ...columnsRenderConfig }, { ...form });
    } else {
      Control = _.get(formValue, name);
    }
  }

  // 受控模式下直接处理value和onChange，不在套用表单逻辑
  if (propsValue || onChange) {
    Control = React.cloneElement(Control as any, {
      value: propsValue,
      onChange,
      style,
    });
    return (
      <ErrorBoundary message={`${label} 渲染异常`}>{Control}</ErrorBoundary>
    );
  }

  // 给每个字段添加key，用户rules变更时刷新校验状态，目前在antd@4.x情况下需要手动刷新
  const key = `${label}${name}${JSON.stringify(_formItemProps?.rules)}`;

  // tooltip提示：表单项问号提示、展开详情提示、图片提示
  const { TooltipUI, toolTipUIShow, formItemPropsTooltip } = useTooltip({
    tooltip: moduleField?.options?.tooltip,
    tooltipMode: moduleField?.options?.tooltipMode,
    mode,
  });

  return (
    <ErrorBoundary message={`${label} 渲染异常`}>
      <Form.Item
        key={key}
        label={label}
        name={name}
        {..._formItemProps}
        style={style}
        className={formItemClassName}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        tooltip={formItemPropsTooltip}
      >
        {Control}
      </Form.Item>
      {toolTipUIShow && <TooltipUI />}
    </ErrorBoundary>
  );
}

export default memo(Field, () => {
  return false; // 必填和disabled的变更都依赖
});
